import ApproveIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import SaveIcon from '@mui/icons-material/Save';
import DisapproveIcon from '@mui/icons-material/UnpublishedOutlined';

import { CellRulePanel, CellRuleStudyId, CellRuleValue, MinimalCellRule } from 'interfaces/cellRule';
import React, { useEffect, useMemo, useState } from 'react';
import useCellRules from 'utils/queryHooks/cellRule/useCellRules';
import CellRulesDataGrid from '.';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getMultiplexPanels, getMultiplexPanelsQueryKey } from 'api/multiplexPanels';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { NON_SPECIFIC_STUDY_VALUES } from 'components/SearchFilters/hooks/useMainFilters';
import { CellType } from 'interfaces/cellType';
import { QCImpactId } from 'interfaces/qcMarkers';
import { compact, difference, filter, find, includes, isEmpty, join, map, uniq } from 'lodash';
import {
  useApproveCellRulePanelMutation,
  useCreateCellRulePanelMutation,
  useDeleteCellRulePanelMutation,
  useUpdateCellRulePanelMutation,
} from 'utils/queryHooks/cellRule/useCellRuleMutations';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import useStudy from 'utils/useStudy';
import CellSelectDropdown from './CellRuleOptions/CellSelectDropdown';
import StainTypeSelectDropdown from './CellRuleOptions/StainTypeDropdown';
import { GLOBAL_PANEL_STUDY_ID } from './GlobalCellRules';

interface CellRulesPanelDataContainerProps {
  studyId: CellRuleStudyId;
  panelId: string;
  editable?: boolean;
  otherApprovedPanelsExist?: boolean;
}

export const CellRulesPanel: React.FC<CellRulesPanelDataContainerProps> = ({
  studyId,
  panelId,
  otherApprovedPanelsExist,
  editable = false,
}) => {
  const confirmWithModal = useConfirmation();
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();

  const { data: panel, isLoading: isLoadingPanel, isError: isErrorLoadingPanel } = useCellRules(panelId, studyId);
  const updatePanelMutation = useUpdateCellRulePanelMutation();
  const createPanelMutation = useCreateCellRulePanelMutation();
  const approvePanelMutation = useApproveCellRulePanelMutation();
  const savedStainTypeIds = uniq(panel?.stainTypeIds);
  const savedCellTypeIds = uniq(map(panel?.rules, 'cellTypeId'));

  const [stainTypeIds, setStainTypeIds] = useState<string[]>(null);
  const [cellTypeIds, setCellTypeIds] = useState<string[]>(null);
  const [rules, setRules] = useState<MinimalCellRule[]>(null);
  const [label, setLabel] = useState(panel?.label || '');
  const [multiplexPanelId, setMultiplexPanelId] = useState<string | null>(panel?.multiplexPanelId ?? null);

  const isSpecificStudySelected = studyId && !includes(NON_SPECIFIC_STUDY_VALUES, studyId.toString());
  const panelScope = useMemo(() => ({ studyId, filters: { studyId }, slidesMode: true as const }), [studyId]);
  const panelsQuery = useQuery({
    queryKey: getMultiplexPanelsQueryKey(panelScope),
    queryFn: ({ signal }) => getMultiplexPanels(panelScope, signal),
    enabled: Boolean(isSpecificStudySelected),
  });
  const multiplexPanelOptions = useMemo(() => {
    const panels = panelsQuery.data?.panels ?? [];
    if (!multiplexPanelId || panels.some((option) => option.panelId === multiplexPanelId)) return panels;
    return [...panels, { panelId: multiplexPanelId, name: 'Saved panel', saved: true, channels: [], slides: [] }];
  }, [multiplexPanelId, panelsQuery.data?.panels]);
  const { study } = useStudy(studyId, { enabled: isSpecificStudySelected });
  const qcMarkersStudySettings = study?.settings?.qcMarkersSettings;

  useEffect(() => {
    // update state when panel is fetched
    if (Boolean(panel) && stainTypeIds === null && cellTypeIds === null && rules === null) {
      setStainTypeIds(uniq(panel?.stainTypeIds));
      setCellTypeIds(uniq(map(panel?.rules, 'cellTypeId')));
      setRules(panel?.rules);
      setLabel(panel?.label);
      setMultiplexPanelId(panel?.multiplexPanelId ?? null);
    }
  }, [panel]);

  const handleCellValueChange = (cellTypeId: string, stainTypeId: string, value: CellRuleValue) => {
    setRules((previousRules) => {
      const existingRule = find(
        previousRules,
        (rule) => rule.cellTypeId === cellTypeId && rule.stainTypeId === stainTypeId
      );
      if (!existingRule) {
        return [...previousRules, { cellTypeId, stainTypeId, ruleValue: value }];
      }
      const updatedRules = map(previousRules, (rule) => {
        if (rule.cellTypeId === cellTypeId && rule.stainTypeId === stainTypeId) {
          return { ...rule, ruleValue: value };
        }
        return rule;
      });
      return updatedRules;
    });
  };

  const handleImport = (newStainTypeIds: string[], newCellTypeIds: string[], newRules: MinimalCellRule[]) => {
    setStainTypeIds(uniq(newStainTypeIds));
    setCellTypeIds(uniq(newCellTypeIds));
    setRules(newRules);
  };

  const getPanelQCMarkers = () => {
    const relevantRules = filter(
      rules,
      (rule) => includes(cellTypeIds, rule.cellTypeId) && includes(stainTypeIds, rule.stainTypeId)
    );

    return {
      [QCImpactId.FLAGGED]: uniq(
        compact(
          map(relevantRules, (rule) =>
            qcMarkersStudySettings?.[rule.stainTypeId] === QCImpactId.FLAGGED
              ? stainTypeIdToDisplayName(rule.stainTypeId) || rule.stainTypeId
              : null
          )
        )
      ),
      [QCImpactId.EXCLUDE]: uniq(
        compact(
          map(relevantRules, (rule) =>
            qcMarkersStudySettings?.[rule.stainTypeId] === QCImpactId.EXCLUDE
              ? stainTypeIdToDisplayName(rule.stainTypeId) || rule.stainTypeId
              : null
          )
        )
      ),
    };
  };

  const confirmPanelContainsQCMarkers = async () => {
    const panelQCMarkers = getPanelQCMarkers();

    if (!isEmpty(panelQCMarkers[QCImpactId.FLAGGED]) || !isEmpty(panelQCMarkers[QCImpactId.EXCLUDE])) {
      return confirmWithModal({
        title: 'This panel contains QC Marker rules',
        text: (
          <Box>
            {!isEmpty(panelQCMarkers[QCImpactId.FLAGGED]) && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                <Box component="span" fontWeight="bold">
                  Flagged QC Markers:
                </Box>{' '}
                {join(panelQCMarkers[QCImpactId.FLAGGED], ', ')}
              </Typography>
            )}
            {!isEmpty(panelQCMarkers[QCImpactId.EXCLUDE]) && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                <Box component="span" fontWeight="bold">
                  Exclude QC Markers:
                </Box>{' '}
                {join(panelQCMarkers[QCImpactId.EXCLUDE], ', ')}
              </Typography>
            )}
            <Typography variant="body1">Are you sure you want to proceed?</Typography>
          </Box>
        ),
      });
    }

    return true;
  };

  const handleSave = async ({ asNew }: { asNew: boolean } = { asNew: false }) => {
    // Only save rules that are relevant to the final stains and cell types (because we dont delete the rules when the stains or cell types are removed)
    const relevantRules = filter(
      rules,
      (rule) => includes(cellTypeIds, rule.cellTypeId) && includes(stainTypeIds, rule.stainTypeId)
    );
    if (await confirmPanelContainsQCMarkers()) {
      if (asNew) {
        createPanelMutation.mutate({ studyId, panel: { label, rules: relevantRules, stainTypeIds } });
      } else {
        updatePanelMutation.mutate({
          panelId,
          panelUpdates: {
            label,
            rules: relevantRules,
            stainTypeIds,
            ...(isSpecificStudySelected ? { multiplexPanelId } : {}),
          },
          studyId,
        });
      }
    }
  };

  const handleApprove = async (approved: boolean) => {
    if (await confirmPanelContainsQCMarkers()) {
      // when approving a panel and there is already an approved panel, confirm with the user
      if (
        !approved ||
        !otherApprovedPanelsExist ||
        (await confirmWithModal({
          title: `Are you sure you want to ${isEmpty(panel?.approvedAt) ? 'approve' : 'disapprove'} this panel? `,
          text: <Typography>Approving this panel will remove approval from any previously approved panel.</Typography>,
        }))
      ) {
        approvePanelMutation.mutate({ approved, panelId, studyId });
      }
    }
  };

  const buttonsDisabled =
    updatePanelMutation.isLoading ||
    createPanelMutation.isLoading ||
    approvePanelMutation.isLoading ||
    isLoadingPanel ||
    isErrorLoadingPanel;

  return isLoadingPanel ? (
    <CellRulesPanelSkeleton />
  ) : isErrorLoadingPanel ? (
    <Typography>Failed to load panel</Typography>
  ) : (
    <>
      <Grid container spacing={2}>
        {editable && (
          <>
            <Grid item xs={12}>
              <TextField label="Name" value={label} onChange={(e) => setLabel(e.target.value)} />
            </Grid>
            {isSpecificStudySelected ? (
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Multiplex panel"
                  value={multiplexPanelId ?? ''}
                  onChange={(event) => setMultiplexPanelId(event.target.value || null)}
                  disabled={panelsQuery.isLoading}
                  helperText="Links this definition to a multiplex panel."
                >
                  <MenuItem value="">None</MenuItem>
                  {multiplexPanelOptions.map((option) => (
                    <MenuItem key={option.panelId} value={option.panelId}>
                      {option.name || 'Saved panel'}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <StainTypeSelectDropdown
                selectedStainTypeIds={stainTypeIds}
                studyId={studyId}
                onSelectStainTypes={(selectedStainTypeIds: string[]) => setStainTypeIds(selectedStainTypeIds)}
                isGlobal={studyId === GLOBAL_PANEL_STUDY_ID}
                limitTags={10}
              />
            </Grid>
            <Grid item xs={12}>
              <CellSelectDropdown
                selectedCellTypeIds={cellTypeIds}
                onSelectCellTypes={(cellTypes: CellType[]) => setCellTypeIds(map(cellTypes, 'id'))}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <CellRulesDataGrid
            adminTableLayout={studyId === GLOBAL_PANEL_STUDY_ID}
            studyId={studyId}
            panelId={panelId}
            isLoading={isLoadingPanel}
            stainTypeIds={stainTypeIds}
            cellTypeIds={cellTypeIds}
            rules={rules}
            editable={editable}
            onCellValueChange={handleCellValueChange}
            onAddCellTypes={(newCellTypeIds) => {
              setCellTypeIds((current) => [...(current || []), ...difference(uniq(newCellTypeIds), current || [])]);
            }}
            onRemoveStainType={(stainTypeId: string) => {
              setStainTypeIds((previousStainTypeIds) => filter(previousStainTypeIds, (id) => id !== stainTypeId));
            }}
            onRemoveCellType={(cellTypeId: string) => {
              setCellTypeIds((previousCellTypeIds) => filter(previousCellTypeIds, (id) => id !== cellTypeId));
            }}
            onImport={handleImport}
          />
        </Grid>
        {editable && (
          <Grid item container>
            <Grid item>
              <Button onClick={() => handleSave()} startIcon={<SaveIcon />} disabled={buttonsDisabled}>
                {updatePanelMutation.isLoading ? 'Saving...' : 'Save'}
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={() => handleSave({ asNew: true })} startIcon={<SaveIcon />} disabled={buttonsDisabled}>
                {createPanelMutation.isLoading ? 'Saving...' : 'Save as new'}
              </Button>
            </Grid>
            <Grid item>
              {isEmpty(panel?.approvedAt) ? (
                <Button onClick={() => handleApprove(true)} startIcon={<ApproveIcon />} disabled={buttonsDisabled}>
                  {approvePanelMutation.isLoading ? 'Approving...' : 'Approve'}
                </Button>
              ) : (
                <Button onClick={() => handleApprove(false)} startIcon={<DisapproveIcon />} disabled={buttonsDisabled}>
                  {approvePanelMutation.isLoading ? 'Disapproving...' : 'Disapprove'}
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button
                onClick={() => {
                  setStainTypeIds(savedStainTypeIds);
                  setCellTypeIds(savedCellTypeIds);
                  setRules(panel?.rules);
                  setMultiplexPanelId(panel?.multiplexPanelId ?? null);
                }}
                disabled={buttonsDisabled}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export const CellRulesPanelSkeleton: React.FC = () => {
  return (
    <Grid item xs={12} container spacing={2}>
      <Grid item xs={12}>
        <Skeleton variant="rounded" width={'100%'} height={35} />
      </Grid>
      <Grid item xs={12}>
        <Skeleton variant="rounded" width={'100%'} height={35} />
      </Grid>
      <Grid item xs={12}>
        <Skeleton variant="rounded" width={'100%'} height={400} />
      </Grid>
    </Grid>
  );
};

interface CellRulesPanelAccordionProps {
  studyId: CellRuleStudyId;
  panel: CellRulePanel;
  otherApprovedPanelsExist?: boolean;
  editable?: boolean;
}

export const CellRulesPanelAccordion: React.FC<CellRulesPanelAccordionProps> = ({
  studyId,
  panel,
  otherApprovedPanelsExist,
  editable = false,
}) => {
  const deleteMutation = useDeleteCellRulePanelMutation();
  const deletePanel = () => {
    deleteMutation.mutate({ studyId, panelId: panel?.id });
  };

  const isApproved = !isEmpty(panel?.approvedAt);

  const formatDate = (date: Date | undefined) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Accordion key={panel?.id}>
      <AccordionSummary>
        <ListItemText>
          <Grid container spacing={1} alignItems="center">
            <Grid item>{isApproved && <PublishIcon color="success" fontSize="small" />}</Grid>
            <Grid item>
              <Typography>{panel?.label}</Typography>
            </Grid>
            <Grid item sx={{ ml: 'auto', mr: 6 }}>
              <Typography variant="caption" color="text.secondary">
                {panel?.updatedAt
                  ? `Modified ${formatDate(panel.updatedAt)}`
                  : `Created ${formatDate(panel?.createdAt)}`}
              </Typography>
            </Grid>
          </Grid>
        </ListItemText>
        <ListItemSecondaryAction>
          {deleteMutation.isLoading ? (
            <CircularProgress size={20} />
          ) : (
            editable && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  deletePanel();
                }}
              >
                <DeleteIcon />
              </IconButton>
            )
          )}
        </ListItemSecondaryAction>
      </AccordionSummary>
      <AccordionDetails>
        <CellRulesPanel
          studyId={studyId}
          panelId={panel?.id}
          editable={editable}
          otherApprovedPanelsExist={otherApprovedPanelsExist}
        />
      </AccordionDetails>
    </Accordion>
  );
};
